<script lang="ts">
  // Ranked leaderboards: the top placed duelists by Glicko rating, plus your own
  // standing pinned at the bottom if you're not in the visible slice. Every ranked
  // mode climbs its OWN ladder — a tab strip switches between them (World default).
  import { fade } from "svelte/transition";
  import { go, session } from "../stores/session.svelte.ts";
  import { send, listen } from "../stores/net.svelte.ts";
  import DataWindow from "../components/ui/DataWindow.svelte";
  import RankBadge from "../components/RankBadge.svelte";
  import { LADDERS, type Ladder, type LeaderboardEntry } from "@ygoevo/world-protocol";

  const TAB_LABEL: Record<Ladder, string> = {
    world: "World",
    "free-single": "Free Singles",
    "free-match": "Free Matches",
  };

  let tab = $state<Ladder>("world");
  let entries = $state<LeaderboardEntry[]>([]);
  let you = $state<{ rankNo: number | null; entry: LeaderboardEntry | null }>({ rankNo: null, entry: null });
  let loaded = $state(false);

  const myId = $derived(session.loginOk?.userId ?? "");
  // Show your row separately only when you're placed but outside the visible slice.
  const youInList = $derived(!!you.entry && entries.some((e) => e.userId === you.entry!.userId));

  listen("Leaderboard", (m) => {
    // The reply echoes its ladder (absent = world): key on it so a slow reply from a
    // previous tab can never render under the wrong one.
    if ((m.ladder ?? "world") !== tab) return;
    entries = m.entries;
    you = m.you;
    loaded = true;
  });

  function pick(l: Ladder): void {
    if (tab === l && loaded) return;
    tab = l;
    entries = [];
    you = { rankNo: null, entry: null };
    loaded = false;
    send({ t: "LeaderboardGet", limit: 100, ladder: l });
  }

  send({ t: "LeaderboardGet", limit: 100 }); // initial load: World (absent ladder = world)

  const wl = (e: LeaderboardEntry) => `${e.wins}–${e.losses}`;
</script>

{#snippet row(e: LeaderboardEntry, mine: boolean)}
  <div
    class="grid grid-cols-[3rem_1fr_auto_auto] items-center gap-3 px-4 py-2.5 transition-colors duration-150 {mine
      ? 'bg-cyan/10 ring-1 ring-cyan/40'
      : 'odd:bg-void-2/30 hover:bg-cyan/[0.07]'}"
  >
    <span class="font-display text-sm font-bold tabular-nums {e.rankNo <= 3 ? 'text-gold' : 'text-ink-dim'}">
      #{e.rankNo}
    </span>
    <span class="min-w-0">
      <span class="block truncate font-display text-sm font-semibold text-ink">{e.name}{mine ? " (you)" : ""}</span>
      <RankBadge rank={{ rating: e.rating, label: e.label, school: e.school }} size="sm" showRating={false} />
    </span>
    <span class="text-right font-body text-xs text-ink-dim tabular-nums">{wl(e)}</span>
    <span class="w-16 text-right font-display text-sm font-bold tabular-nums text-cyan">{e.rating}</span>
  </div>
{/snippet}

<div class="mobile-dialog-host grid h-full min-h-0 place-items-center p-4" transition:fade={{ duration: 160 }}>
  <div class="mobile-full-dialog w-[min(620px,94vw)]">
    <DataWindow c={18} fill class="mobile-dialog-panel">
      <!-- header -->
      <div class="flex items-center justify-between border-b border-cyan-dim/40 bg-surface-2/70 px-5 py-3">
        <span class="flex items-center gap-2">
          <span class="size-1.5 rounded-full bg-cyan animate-pulse-soft" style="filter:drop-shadow(0 0 5px var(--color-cyan))"></span>
          <span class="font-display text-[0.8rem] font-semibold uppercase tracking-[0.26em] text-cyan">Leaderboard</span>
        </span>
        <button
          onclick={() => go("world")}
          class="fx-lift fx-sweep chamfer-btn bg-void-2/50 px-4 py-1.5 font-display text-xs font-semibold uppercase tracking-widest text-ink-dim hover:bg-surface-2 hover:text-cyan"
          style="--c:7px"
        >
          Back
        </button>
      </div>

      <!-- ladder tabs (each ranked mode has its own ladder) -->
      <div class="flex flex-wrap gap-1 border-b border-cyan-dim/25 bg-surface-2/40 px-3 py-2" role="tablist" aria-label="Ladder">
        {#each LADDERS as l (l)}
          <button
            role="tab"
            aria-selected={tab === l}
            onclick={() => pick(l)}
            class="chamfer-btn px-3 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-[0.14em] transition-colors {tab === l
              ? 'bg-cyan/15 text-cyan ring-1 ring-cyan/50'
              : 'text-ink-dim hover:bg-surface-2 hover:text-ink'}"
            style="--c:5px"
          >
            {TAB_LABEL[l]}
          </button>
        {/each}
      </div>

      <!-- column heads -->
      <div class="grid grid-cols-[3rem_1fr_auto_auto] gap-3 border-b border-cyan-dim/15 px-4 py-2 font-display text-[0.62rem] uppercase tracking-[0.2em] text-ink-dim/70">
        <span>Rank</span>
        <span>Duelist</span>
        <span class="text-right">W–L</span>
        <span class="w-16 text-right">Rating</span>
      </div>

      <!-- rows -->
      <div class="mobile-dialog-scroll max-h-[62dvh] overflow-y-auto scroll-cyber">
        {#if !loaded}
          <div class="px-5 py-10 text-center font-body text-sm text-ink-dim">Loading rankings…</div>
        {:else if entries.length === 0}
          <div class="px-5 py-10 text-center font-body text-sm text-ink-dim">
            No ranked duelists yet, win 5 ranked duels to place on the ladder.
          </div>
        {:else}
          {#each entries as e (e.userId)}
            {@render row(e, e.userId === myId)}
          {/each}
        {/if}
      </div>

      <!-- your standing, if outside the visible slice -->
      {#if loaded && you.entry && !youInList}
        <div class="border-t border-cyan-dim/25">
          {@render row(you.entry, true)}
        </div>
      {:else if loaded && !you.entry}
        <div class="border-t border-cyan-dim/25 px-5 py-3 text-center font-body text-xs text-ink-dim">
          You're still in placement, finish 5 ranked duels to join the ladder.
        </div>
      {/if}
    </DataWindow>
  </div>
</div>
